import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Heading2, Eraser, AlignLeft, AlignCenter, AlignRight, AlignJustify } from "lucide-react";

const TOOLS = [
  ["bold", Bold], ["italic", Italic], ["underline", Underline], ["formatBlock:h3", Heading2],
  ["insertUnorderedList", List], ["insertOrderedList", ListOrdered], ["link", Link2], ["removeFormat", Eraser],
];

const ALIGN_TOOLS = [
  ["justifyLeft", AlignLeft, "Align left"], ["justifyCenter", AlignCenter, "Align center"],
  ["justifyRight", AlignRight, "Align right"], ["justifyFull", AlignJustify, "Justify"],
];

export const RichEditor = ({ value, onChange, testid = "rich-editor", minHeight = 140, maxHeight, align = false }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
  }, [value]);

  const run = (cmd) => {
    ref.current?.focus();
    if (cmd === "link") {
      const url = window.prompt("Link URL");
      if (url) document.execCommand("createLink", false, url);
    } else if (cmd.startsWith("formatBlock")) {
      document.execCommand("formatBlock", false, cmd.split(":")[1]);
    } else {
      const justify = cmd.startsWith("justify");
      document.execCommand("defaultParagraphSeparator", false, "p");
      document.execCommand("styleWithCSS", false, justify);
      document.execCommand(cmd, false, null);
      if (justify) {
        ref.current.querySelectorAll("[style]").forEach((s) => {
          s.style.removeProperty("font-size");
          if (!s.getAttribute("style").trim()) {
            if (s.tagName === "SPAN") s.replaceWith(...s.childNodes);
            else s.removeAttribute("style");
          }
        });
      }
    }
    onChange(ref.current.innerHTML);
  };

  return (
    <div className="border border-stone-300 rounded-md bg-white focus-within:ring-2 focus-within:ring-crimson/30" data-testid={testid}>
      <div className="flex flex-wrap gap-0.5 border-b border-stone-200 p-1 bg-stone-50 rounded-t-md">
        {TOOLS.map(([cmd, Icon]) => (
          <button key={cmd} type="button" onMouseDown={(e) => { e.preventDefault(); run(cmd); }} className="p-1.5 rounded hover:bg-stone-200 text-stone-700" data-testid={`${testid}-tool-${cmd.replace(":", "-")}`} title={cmd}>
            <Icon className="w-4 h-4" />
          </button>
        ))}
        {align && <span className="w-px bg-stone-300 mx-1 my-1" aria-hidden />}
        {align && ALIGN_TOOLS.map(([cmd, Icon, label]) => (
          <button key={cmd} type="button" onMouseDown={(e) => { e.preventDefault(); run(cmd); }} className="p-1.5 rounded hover:bg-stone-200 text-stone-700" data-testid={`${testid}-tool-${cmd}`} title={label} aria-label={label}>
            <Icon className="w-4 h-4" />
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="editor-area rich px-3 py-2.5 text-sm outline-none overflow-y-auto"
        style={{ minHeight, maxHeight }}
        data-placeholder="Start typing…"
        data-testid={`${testid}-input`}
      />
    </div>
  );
};
